import { useEffect, useMemo, useState, useImperativeHandle, forwardRef } from 'react';
import { isEqual } from 'lodash-es';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import type { TypeTimeoutConfig, TypeTimeoutConfigForm } from '@intone/shared/types';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfigResetButtons } from '@/components/ConfigResetButtons';
import {
  FormSliderField,
} from '@/components/form-fields';

import { useTimeoutConfigs, useSaveTimeoutConfig } from '../model/useTimeoutConfigs';

interface TimeoutConfigurationBlockProps {
  agentId: string;
  selectedId: number;
  onHasChanges?: (hasChanges: boolean) => void;
  readOnly?: boolean;
  headerRight?: React.ReactNode;
}

export interface TimeoutConfigurationBlockRef {
  save: () => Promise<boolean>;
  hasChanges: () => boolean;
}

const FIELD_DESCRIPTIONS = {
  first_turn_timeout: 'Time to wait for the first response of the customer before terminating the call',
  other_turns_timeout: 'Time to wait before asking the user a follow-up message (e.g. "are you still there?")',
  confirmation_timeout: 'Time to wait before terminating the call after follow-up question',
};

const mapToForm = (config?: TypeTimeoutConfig): TypeTimeoutConfigForm => ({
  name: config?.name ?? 'Timeout Config',
  first_turn_timeout: config?.first_turn_timeout ?? 5.0,
  other_turns_timeout: config?.other_turns_timeout ?? 5.0,
  confirmation_timeout: config?.confirmation_timeout ?? 5.0,
});

export const TimeoutConfigurationBlock = forwardRef<
  TimeoutConfigurationBlockRef,
  TimeoutConfigurationBlockProps
>(function TimeoutConfigurationBlock({ agentId, selectedId, onHasChanges, readOnly, headerRight }, ref) {
  const queryClient = useQueryClient();
  const { data: timeoutConfigs, isPending, isFetching, isSuccess } = useTimeoutConfigs(selectedId);
  const saveConfig = useSaveTimeoutConfig();

  const [currentConfig, setCurrentConfig] = useState<TypeTimeoutConfigForm>(mapToForm());

  const defaultConfig: TypeTimeoutConfigForm = useMemo(
    () => mapToForm(timeoutConfigs?.find(c => c.id === 1)),
    [timeoutConfigs]
  );

  const selectedConfig: TypeTimeoutConfigForm = useMemo(
    () => mapToForm(timeoutConfigs?.find((c) => c.id === selectedId)),
    [timeoutConfigs, selectedId]
  );

  useEffect(() => {
    if (isSuccess && !isFetching) {
      setCurrentConfig(selectedConfig);
    }
  }, [selectedId, isSuccess, isFetching]);

  const hasChanges = useMemo(() => !isEqual(currentConfig, selectedConfig), [currentConfig, selectedConfig]);

  useEffect(() => {
    onHasChanges?.(hasChanges);
  }, [hasChanges, onHasChanges]);

  const updateConfig = <K extends keyof TypeTimeoutConfigForm>(
    field: K,
    value: TypeTimeoutConfigForm[K]
  ) => {
    setCurrentConfig((prev) => ({ ...prev, [field]: value }));
  };

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (!hasChanges) return true;

      if (!currentConfig.name) {
        toast.error('Timeout: Config name is required');
        return false;
      }

      return new Promise((resolve) => {
        saveConfig.mutate(
          { id: selectedId === 1 ? undefined : selectedId, agentId, ...currentConfig },
          {
            onSuccess: () => {
              if (selectedId === 1) {
                queryClient.invalidateQueries({ queryKey: ['engineAgent', agentId] });
              }
              resolve(true);
            },
            onError: () => {
              resolve(false);
            },
          }
        );
      });
    },
    hasChanges: () => hasChanges,
  }), [hasChanges, currentConfig, selectedId, agentId, saveConfig, queryClient]);

  if (isPending) {
    return null;
  }

  return (
    <Card className={`border-gray-300 ${isFetching ? 'opacity-70' : ''}`}>
      <CardHeader className="bg-gray-50 border-b border-gray-300 py-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-body font-semibold">Timeout Configuration</CardTitle>
          <div className="flex items-center gap-2">
            {!readOnly && (
              <ConfigResetButtons
                hasChanges={hasChanges}
                showRestore={selectedId !== 1}
                isRestoreDisabled={isEqual(currentConfig, defaultConfig)}
                onReset={() => setCurrentConfig(selectedConfig)}
                onRestoreDefault={() => setCurrentConfig(defaultConfig)}
              />
            )}
            {headerRight}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6 grid gap-4">
        <div className={`space-y-4 ${readOnly ? 'opacity-70 pointer-events-none' : ''}`}>
          <div className="grid grid-cols-2 gap-4">
            <FormSliderField
              id="first-turn-timeout"
              label="First Turn Timeout"
              description={FIELD_DESCRIPTIONS.first_turn_timeout}
              value={currentConfig.first_turn_timeout}
              onChange={(v) => updateConfig('first_turn_timeout', v)}
              rangeMin={3}
              rangeMax={120}
              step={1}
            />

            <FormSliderField
              id="other-turns-timeout"
              label="Wait Timeout"
              description={FIELD_DESCRIPTIONS.other_turns_timeout}
              value={currentConfig.other_turns_timeout}
              onChange={(v) => updateConfig('other_turns_timeout', v)}
              rangeMin={3}
              rangeMax={120}
              step={1}
            />

            <FormSliderField
              id="confirmation-timeout"
              label="Termination Timeout"
              description={FIELD_DESCRIPTIONS.confirmation_timeout}
              value={currentConfig.confirmation_timeout}
              onChange={(v) => updateConfig('confirmation_timeout', v)}
              rangeMin={3}
              rangeMax={120}
              step={1}
            />
          </div>
        </div>

      </CardContent>
    </Card>
  );
});
